// frontend/src/utils/currency.ts

/**
 * Supported currencies in the application
 */
export const SUPPORTED_CURRENCIES = ['NGN', 'USD', 'GHS', 'KES', 'ZAR', 'XOF'] as const;

/**
 * Currency type definition
 */
export type SupportedCurrency = typeof SUPPORTED_CURRENCIES[number];

/**
 * Currency symbols for each supported currency
 */
export const CURRENCY_SYMBOLS: Record<SupportedCurrency, string> = {
  NGN: '₦',
  USD: '$',
  GHS: '₵',
  KES: 'KSh',
  ZAR: 'R',
  XOF: 'CFA'
};

/**
 * Currency formatting options for each supported currency
 */
export const CURRENCY_FORMAT_OPTIONS: Record<SupportedCurrency, Intl.NumberFormatOptions> = {
  NGN: { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 },
  USD: { style: 'currency', currency: 'USD', minimumFractionDigits: 2 },
  GHS: { style: 'currency', currency: 'GHS', minimumFractionDigits: 2 },
  KES: { style: 'currency', currency: 'KES', minimumFractionDigits: 0 },
  ZAR: { style: 'currency', currency: 'ZAR', minimumFractionDigits: 2 },
  XOF: { style: 'currency', currency: 'XOF', minimumFractionDigits: 0 }
};

/**
 * Format a price with the appropriate currency symbol
 */
export function formatPrice(amount: number, currency: SupportedCurrency = 'NGN'): string {
  try {
    // Use Intl.NumberFormat for proper formatting
    return new Intl.NumberFormat('en-US', CURRENCY_FORMAT_OPTIONS[currency]).format(amount);
  } catch (error) {
    // Fallback format if Intl formatting fails
    return `${CURRENCY_SYMBOLS[currency]}${amount.toLocaleString()}`;
  }
}

/**
 * Format a price for display without using Intl.NumberFormat
 * Used as a more reliable alternative when we need consistent display
 */
export function formatPriceSimple(amount: number, currency: SupportedCurrency = 'NGN'): string {
  const symbol = CURRENCY_SYMBOLS[currency] || '';
  
  // Format based on currency
  if (currency === 'USD' || currency === 'GHS' || currency === 'ZAR') {
    // Add two decimal places for these currencies
    return `${symbol}${amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  } else {
    // No decimal places for others
    return `${symbol}${Math.round(amount).toLocaleString()}`;
  }
}

/**
 * Detect user's preferred currency based on locale and defaults
 */
export function detectUserCurrency(): SupportedCurrency {
  try {
    // Get user's locale from browser
    const userLocale = navigator.language;
    
    // Map common locales to currencies
    if (userLocale.includes('en-US')) return 'USD';
    if (userLocale.includes('en-NG')) return 'NGN';
    if (userLocale.includes('en-GH')) return 'GHS';
    if (userLocale.includes('en-KE')) return 'KES';
    if (userLocale.includes('en-ZA')) return 'ZAR';
    
    // Default to NGN for now
    return 'NGN';
  } catch (error) {
    return 'NGN';
  }
}

/**
 * Convert price between currencies (placeholder - would need exchange rates in real implementation)
 */
export function convertCurrency(
  amount: number, 
  fromCurrency: SupportedCurrency, 
  toCurrency: SupportedCurrency
): number {
  // This is a placeholder. In a real application, you would:
  // 1. Store exchange rates in your database
  // 2. Or use an API to get current exchange rates
  // 3. Apply the conversion formula
  
  if (fromCurrency === toCurrency) return amount;
  
  // Example placeholder conversion (not for production use)
  const rates: Record<SupportedCurrency, number> = {
    USD: 1,
    NGN: 1700,
    GHS: 14,
    KES: 130,
    ZAR: 18,
    XOF: 600
  };
  
  // Convert to USD first, then to target currency
  const amountInUSD = fromCurrency === 'USD' ? amount : amount / rates[fromCurrency];
  return toCurrency === 'USD' ? amountInUSD : amountInUSD * rates[toCurrency];
}

/**
 * Get amount in the smallest currency unit (e.g., kobo for NGN, cents for USD)
 * This is important for payment processors like Paystack
 */
export function getAmountInMinorUnit(amount: number, currency: SupportedCurrency = 'NGN'): number {
  switch (currency) {
    case 'USD':
    case 'GHS':
    case 'ZAR':
    case 'NGN':
      // These currencies use 100 as the conversion factor (e.g., 100 cents = 1 USD)
      return Math.round(amount * 100);
    case 'KES':
    case 'XOF':
      // These currencies don't have minor units in practical use with Paystack
      return Math.round(amount);
    default:
      return Math.round(amount * 100);
  }
}